import React, { useEffect, useMemo, useState } from 'react'
import Image from 'next/image'
import clsx from 'clsx'

import { Text } from '@wartek-id/text-v1'

import {
  CALENDAR_STATUS_BY_STATE,
  CALENDAR_STATUS_CONFIG,
  MONTHS,
  WEEKDAY_LABELS,
} from 'app/KinerjaHarian/constants'

import type { CalendarStatus } from 'app/KinerjaHarian/constants'
import type {
  KinerjaHarianLog,
  KinerjaHarianLogState,
} from 'types/kinerja-harian'

type CalendarDay = {
  date: string
  dayNumber: number
  status: CalendarStatus
  isSelected: boolean
}

function getCalendarStatus(state?: KinerjaHarianLogState): CalendarStatus {
  return (state && CALENDAR_STATUS_BY_STATE[state]) || 'EMPTY'
}

interface KinerjaHarianCalendarViewProps {
  items: KinerjaHarianLog[]
  selectedMonthOption: string
  onClick?: (item: KinerjaHarianLog | null) => void
}

export function KinerjaHarianCalendarView({
  items,
  selectedMonthOption,
  onClick,
}: KinerjaHarianCalendarViewProps) {
  const [selectedDate, setSelectedDate] = useState<string | null>(null)

  useEffect(() => {
    setSelectedDate(null)
  }, [selectedMonthOption])

  const calendarData = useMemo(() => {
    if (selectedMonthOption === 'all') {
      return null
    }

    const [monthValue, yearValue] = selectedMonthOption.split('-')
    const monthIndex = MONTHS.findIndex(
      (month) => month.toLowerCase() === monthValue
    )

    if (monthIndex < 0) {
      return null
    }

    const year = Number(yearValue)
    const firstDate = new Date(year, monthIndex, 1)
    const totalDays = new Date(year, monthIndex + 1, 0).getDate()
    const itemMap = new Map(items.map((item) => [item.entryDate, item]))
    const days: CalendarDay[] = Array.from(
      { length: totalDays },
      (_, index) => {
        const dayNumber = index + 1
        const date = `${year}-${String(monthIndex + 1).padStart(
          2,
          '0'
        )}-${String(dayNumber).padStart(2, '0')}`
        const item = itemMap.get(date)

        return {
          date,
          dayNumber,
          status: getCalendarStatus(item?.state),
          isSelected: selectedDate === date,
        }
      }
    )

    return {
      firstWeekday: firstDate.getDay(),
      days,
    }
  }, [items, selectedDate, selectedMonthOption])

  useEffect(() => {
    if (!calendarData) {
      setSelectedDate(null)
    }
  }, [calendarData])

  if (!calendarData) {
    return (
      <div className="flex min-h-[420px] flex-col items-center justify-center rounded-lg bg-white text-center">
        <Image
          alt=""
          src="/illustrations/img-to-do-list-document.png"
          width={88}
          height={88}
        />
        <Text color="subdued" className="mt-5 max-w-[560px] text-lg">
          Pilih bulan terlebih dahulu untuk menampilkan kalender.
        </Text>
      </div>
    )
  }

  const { firstWeekday, days } = calendarData
  const leadingEmptyDays = Array.from({ length: firstWeekday }, (_, index) => (
    <div key={`empty-${index}`} />
  ))

  const handleDateClick = (date: string) => {
    setSelectedDate(date)
    onClick?.(items.find((item) => item.entryDate === date) ?? null)
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-7 gap-1">
        {WEEKDAY_LABELS.map((weekday) => (
          <div key={weekday} className="px-2 py-3 text-center">
            <Text variant="body-bold" className="text-sm text-gray-90">
              {weekday}
            </Text>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {leadingEmptyDays}
        {days.map((day) => {
          const config = CALENDAR_STATUS_CONFIG[day.status]
          return (
            <button
              key={day.date}
              type="button"
              className={clsx(
                'relative flex h-[108px] flex-col items-center justify-center rounded-sm border text-center transition',
                config.cellClassName,
                day.isSelected
                  ? 'border-blue-70'
                  : 'border-transparent hover:border-gray-30'
              )}
              onClick={() => handleDateClick(day.date)}
            >
              <Text className="text-base font-medium">{day.dayNumber}</Text>
              {config.dotClassName && (
                <span
                  className={clsx(
                    'absolute bottom-3 h-1.5 w-1.5 rounded-full',
                    config.dotClassName
                  )}
                />
              )}
            </button>
          )
        })}
      </div>

      <div className="border-t border-gray-30 pt-4">
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 max-w-2xl mx-auto">
          {Object.keys(CALENDAR_STATUS_CONFIG).map((key) => {
            const config = CALENDAR_STATUS_CONFIG[key as CalendarStatus]
            return (
              <div key={key} className="flex items-center gap-2">
                <span
                  className={clsx('h-3 w-3 rounded-sm', config.cellClassName)}
                />
                <Text className="text-sm text-gray-90">{config.label}</Text>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
